import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import PageLoader from 'components/PageLoader';
import SettingsHeader from './components/SettingsHeader';
import SettingsForm from './components/SettingsForm';
import styles from './styles';
import { useForm, FormProvider } from 'react-hook-form';
import { getDwlUsers, getLocations, getSettings, patchSettings } from 'store/settings/settingsOperations';
import { filterDataByTab } from './settings-requests.helper';
import SnackBar from 'components/SnackBar';
import OptionalModal from 'components/OptionalModal/OptionalModal';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { convertTimeObject } from 'common/dateFormatConfig';
import { isAvailabilityEnabled } from 'common/permissions';

const options = {
  title: 'Are you sure you want to exit without changes?',
  text: '',
  cancelBtnText: 'Discard',
  saveBtnText: 'Save',
};

const SettingsPage = ({ classes }) => {
  const getTimeFormatHook = useTimeSettingsFormat();
  const is12Format = getTimeFormatHook().is12Format;
  const [pageType, setPageType] = useState('edit');
  const [submitting, setSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [modal, setModal] = useState({
    isOpen: false,
    route: null,
  });
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 999px)');

  const locationSettings = useSelector((state) => state.personalProfile.organizationLocation?.settings);
  const organizationLocation = useSelector((state) => state.personalProfile.organizationLocation);
  const organizationSettings = useSelector(state => state.settings.settings);

  const defaultSettings = useMemo(
    () => ({
      ...locationSettings,
      lateClockInEmails: locationSettings?.lateClockInEmails || [],
      injuryReportEmails: locationSettings?.injuryReportEmails || [],
      homePageQuote: locationSettings?.homepage?.quote || '',
      homePageQuoteColor: locationSettings?.homepage?.quoteColor || '',
      countdownDate: locationSettings?.homepage?.countdownDate || '',
      countdownTitle: locationSettings?.homepage?.countdownTitle || '',
      countdownEnabled: locationSettings?.homepage?.countdownEnabled || false,
      defaultDayTime: convertTimeObject(
        locationSettings?.defaultDayTime || { hour: '06', minute: '00' },
        is12Format
      ),
      defaultNightTime: convertTimeObject(
        locationSettings?.defaultNightTime || { hour: '1', minute: '00' },
        is12Format
      ),
      headquartersLocation: organizationLocation.address,
      businessFilingCabinet: organizationSettings?.businessFilingCabinet || false,
      navigationSystem: organizationSettings?.navigationSystem || 'google',
      availabilityCalendar: isAvailabilityEnabled(locationSettings),
    }),
    [locationSettings, organizationSettings]
  );
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: defaultSettings || {},
    shouldUnregister: false,
  });
  const {
    formState: { errors },
    handleSubmit,
    getValues,
    control,
    reset,
    onChange,
    formState: { isDirty },
    watch,
  } = methods;
  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    text: '',
    type: 'success',
  });

  const openSnackbar = useCallback((type, text) => setSnackbar({ isOpen: true, type, text }), []);
  const closeSnackbar = useCallback(() => setSnackbar((p) => ({ ...p, isOpen: false })), []);

  useEffect(() => {
    dispatch(getSettings());
    dispatch(getLocations({ activeUsers: true }))
    dispatch(getDwlUsers());
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    isDirty && window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!locationSettings?.homepage) return;
    reset(defaultSettings);
  }, [locationSettings, organizationSettings, reset]);

  const handleChange = (event, newValue) => {
    if (isDirty) {
      setModal((prev) => ({ ...prev, isOpen: true, route: newValue }));
    } else {
      setTabValue(newValue);
    }
  };

  const onCancel = () => {
    const locationTabValue = 5 + Number(!!getValues()?.timeclock);
    if (tabValue !== locationTabValue) reset(defaultSettings);
    setSubmitting(false);
  }

  const closeEdit = () => {
    setSubmitting(false);
  };

  const onSubmit = async () => {
    const values = getValues();

    const result = filterDataByTab(tabValue, values, organizationSettings);
    if (!result) {
      openSnackbar('error', 'Method to update settings was not found')
      return
    };

    setSubmitting(true);

    await dispatch(patchSettings(result))
      .then((settings) => {
        openSnackbar('success', 'Successfully Saved!');
        closeEdit();
        setModal((prev) => ({ ...prev, isOpen: false, route: null }));
      })
      .catch((e) => openSnackbar('error', e?.response?.data?.message || e.message));
  };

  if (!locationSettings) return null;

  const timeclock = watch('timeclock');
  const tabShift = Number(!!timeclock);

  return (
    <div className={classes.root}>
      <SettingsHeader pageType={pageType} changePageType={setPageType} />
      <PageLoader loading={submitting}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={classes.formFlex}>
            <Grid container spacing={isMobile ? 2 : 5}>
              <SettingsForm
                pageType={pageType}
                control={control}
                errors={errors}
                settings={locationSettings}
                onChange={onChange}
                tabValue={tabValue}
                handleChange={handleChange}
                getValues={getValues}
                watch={watch}
                openSnackbar={openSnackbar}
              />
            </Grid>
            {pageType === 'edit' && (
              <div className={classes.buttonHolder}>
                <Button
                  color="secondary"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={onCancel}
                  disabled={submitting}
                  disableTouchRipple
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  disabled={[4 + tabShift, 5 + tabShift].includes(tabValue) || submitting}
                  disableTouchRipple
                >
                  Save
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </PageLoader>
      <SnackBar closeSnackbar={closeSnackbar} {...snackbar} />
      <OptionalModal
        isOpen={modal.isOpen}
        onClose={() => {
          setModal((prev) => ({ ...prev, isOpen: false }));
        }}
        onCancel={(e) => {
          closeEdit();
          reset(defaultSettings);
          setTabValue(modal?.route);
          setModal((prev) => ({ ...prev, isOpen: false }));
        }}
        onSave={() => {
          handleSubmit(onSubmit)();
          setTabValue(modal?.route);
        }}
        options={options}
      />
    </div>
  );
};

SettingsPage.propTypes = {
  classes: propTypes.object.isRequired,
};

export default withStyles(styles)(SettingsPage);
